import { Prisma } from "@prisma/client";
import { z } from "zod";
import { ProfileEditRequiredData } from "~/components/rsvp/validators";

export type UserFull = Prisma.UserGetPayload<{
    include: {
        managedCommunities: true;
        hostedEvents: true;
        preapproval: true;
    };
}>;
const twitterHandleSchema = z.string().refine(
    (handle) => {
        return /^[A-Za-z0-9_]{1,15}$/.test(handle);
    },
    {
        message:
            "Invalid Twitter handle format. It should contain only alphanumeric characters and underscores, with a length between 1 and 15 characters.",
    },
);
const addProtocolIfMissing = (
    socialNetwork: "li" | "ig" | "fb",
    urlOrHandle: string,
) => {
    if (!urlOrHandle) return urlOrHandle;
    if (!urlOrHandle.startsWith("http"))
        switch (socialNetwork) {
            case "ig":
                return `https://www.instagram.com/${urlOrHandle}`;
            case "li":
                return `https://www.linkedin.com/in/${urlOrHandle}`;
            case "fb":
                return `https://www.facebook.com/${urlOrHandle}`;
        }

    return urlOrHandle;
};

const urlOrEmptyString = (socialNetwork: "li" | "ig" | "fb") =>
    z
        .string()
        .transform(addProtocolIfMissing.bind(null, socialNetwork))
        .pipe(z.string().url())
        .nullish()
        .or(z.literal(""));

export const socialLinksValidator = z.object({
    // twitter handle validator
    x: twitterHandleSchema.nullish().or(z.literal("")),
    li: urlOrEmptyString("li"),
    ig: urlOrEmptyString("ig"),
    fb: urlOrEmptyString("fb"),
});
export type SocialLinks = z.infer<typeof socialLinksValidator>;

export const chatLinksValidator = z.object({
    whatsapp: z.string().nullish(),
    telegram: z.string().nullish(),
    discord: z.string().nullish(),
});
export type ChatLinks = z.infer<typeof chatLinksValidator>;

export const profileFormSchema = z.object({
    userId: z.string(),
    images: z.object({
        image: z.string().optional(),
        bannerImage: z.string().optional(),
        organizationImage: z.string().optional(),
    }),
    firstName: z
        .string({
            required_error: "First name is required",
        })
        .max(50, "First name must be less than 50 characters"),
    lastName: z
        .string()
        .max(50, "Last name must be less than 50 characters")
        .optional(),
    slug: z
        .string()
        .min(5, "Profile URL must be more than 5 characters ")
        .max(30, "Profile URL must be less than 50 characters")
        .optional(),
    email: z.string().email("Invalid email format").optional(),
    phone: z.string().optional(),
    socialLinks: socialLinksValidator.optional(),
    chatLinks: chatLinksValidator,
    bio: z
        .string()
        .max(2048, "About section must be less than 500 characters")
        .optional(),
    organizationName: z.string().optional(),
    organizationLink: z.string().optional(),
    useOrganizationIdentity: z.boolean().optional(),
});

export const profileFormSchemaCustomLimits = (
    options?: ProfileEditRequiredData | null | undefined,
) => {
    const defaults = {
        minNameLength: 1,
        minBioLength: 0,
        imageRequired: true,
    };
    const minNameLength = options?.minNameLength ?? defaults.minNameLength;
    const bio = options?.minBioLength
        ? z
              .string({
                  required_error: "About section is required",
              })
              .min(
                  options.minBioLength,
                  `About section should have more than ${options.minBioLength} characters`,
              )
              .max(2048, "About section must be less than 500 characters")
        : z
              .string()
              .min(0)
              .max(2048, "About section must be less than 500 characters")
              .optional();
    const imageRequired = options?.imageRequired ?? defaults.imageRequired;
    const phoneRequired = options?.phoneNumber
        ? z.string({
              required_error:
                  "Phone number is required to host an event at this community.",
          })
        : z.string().optional();

    return z.object({
        userId: z.string(),
        images: z.object({
            image: imageRequired ? z.string() : z.string().optional(),
            bannerImage: z.string().optional(),
            organizationImage: z.string().optional(),
        }),
        firstName: z
            .string({
                required_error: "First name is required",
            })
            .min(
                minNameLength,
                `Name should be atleast ${minNameLength} characters`,
            )
            .max(50, "First name must be less than 50 characters"),
        lastName: z
            .string()
            .max(50, "Last name must be less than 50 characters")
            .optional(),
        slug: z
            .string()
            .min(5, "Profile URL must be more than 5 characters ")
            .max(30, "Profile URL must be less than 50 characters")
            .optional(),
        email: z.string().email("Invalid email format").optional(),
        phone: phoneRequired,
        chatLinks: chatLinksValidator.optional(),
        socialLinks: socialLinksValidator.optional(),
        bio,
        organizationName: z.string().optional(),
        organizationLink: z.string().optional(),
        useOrganizationIdentity: z.boolean().optional(),
    });
};

export type ProfileFormType = z.infer<typeof profileFormSchema>;
